import { Alert, Form, Typography } from "antd";
import ActionMenuV2 from "components/ActionMenuV2";
import { OkButtonProps } from "components/BaseModal";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import TableContainer from "components/TableContainer";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _, { isEmpty, isNil } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";
import { CellRenderers, columnRenderArrayData } from "utils/tableUtils";

import { CMDBSchemaType } from "../JiraCMDB/JiraCMDBMappingForm";

type ServiceNowCmdbConfigListProps = {
  data?: ServiceNowCmdbDataProps[];
  onEditConfig?: (data: any) => void;
  onUpdateConfigComponent?: () => any;
  loginConfigId: number;
};

type ServiceNowCmdbOptionsProps = {
  label: string;
  value: string;
};

type ServiceNowCmdbDataProps = {
  discoveryType?: string;
  schema?: CMDBSchemaType;
  attributes?: string[];
  linkedTables?: string[];
  linkedTableAttributes?: string[];
  cmdbQuery?: string;
  id?: number;
  multiLevelAttributes?: string;
};

type ServiceNowCmdbFormProps = {
  data?: ServiceNowCmdbDataProps;
  loginConfigId?: any;
  onUpdateConfigComponent?: () => any;
  okButtonProps?: OkButtonProps;
};

type TableContainerProps = {
  dataSource?: ServiceNowCmdbDataProps[];
  columns?: any[];
  enableScroll?: boolean;
};

type ActionMenuProps = {
  items?: any[];
  onClick?: (event: any, record: any) => void;
  data?: any[];
};

const ServiceNowCmdbMappingForm = (
  { data, loginConfigId, ...props }: ServiceNowCmdbFormProps,
  ref: React.ForwardedRef<any>,
) => {
  const [cmdbCiTableOptions, setCmdbCiTableOptions] =
    useState<ServiceNowCmdbOptionsProps[]>();
  const [cmdbCiAttributeOptions, setCmdbCiAttributeOptions] =
    useState<ServiceNowCmdbOptionsProps[]>();
  const [formData, setFormData] = useState<ServiceNowCmdbDataProps>(() => {
    return {
      ...data,
      tables: data?.schema?.name ? data.schema.name : undefined,
      discoveryType: data?.discoveryType
        ? _.capitalize(data?.discoveryType)
        : "Asset",
    };
  });
  const [linkedTableOptions, setLinkedTableOptions] =
    useState<ServiceNowCmdbOptionsProps[]>();
  const [linkedAttributesOptions, setLinkedAttributesOptions] =
    useState<ServiceNowCmdbOptionsProps[]>();
  const [showLinkedTableWarning, setShowLinkedTableWarning] =
    useState<boolean>(false);
  const [selectedSchema, setSelectedSchema] = useState<CMDBSchemaType>(
    () => data?.schema ?? {},
  );
  const [form]: any = Form.useForm();
  const isEdit = !isNil(data?.discoveryType);
  const featureFlags = useFeatureFlag();

  //get cmdb ci table options
  useEffect(() => {
    const getCmdbDiscoveryTableOptions = () => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getServiceNowCmdbCiTables({
          ticketSystemType: "SERVICE_NOW",
          id: loginConfigId,
        });
      promise.then((response) => {
        const tableoptions = response?.[0].tables;
        setCmdbCiTableOptions(
          _.map(tableoptions, (t) => ({ label: t, value: t })),
        );
      });

      return cancel;
    };

    const tableApiCancel = getCmdbDiscoveryTableOptions();

    return () => tableApiCancel();
  }, [loginConfigId]);

  useEffect(() => {
    if (selectedSchema.name) {
      const getCmdbDiscoveryAttributeOptions = () => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getServiceNowCmdbCiAttributes({
            id: loginConfigId,
            ticketSystemType: "SERVICE_NOW",
            tables: [selectedSchema.name],
          });
        promise.then((response) => {
          setCmdbCiAttributeOptions(
            _.map(response.attributes, (a) => ({ label: a, value: a })),
          );
        });

        return cancel;
      };

      const attributeApiCancel = getCmdbDiscoveryAttributeOptions();
      return () => attributeApiCancel();
    }
  }, [selectedSchema.name, loginConfigId]);

  //get linked table options
  useEffect(() => {
    if (formData.discoveryType === "Application" && selectedSchema.name) {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getServiceNowCmdbCiAttributes({
          id: loginConfigId,
          ticketSystemType: "SERVICE_NOW",
          tables: [selectedSchema.name],
        });
      promise.then((response) => {
        if (response.fetchedFromSysDictionary === false) {
          setShowLinkedTableWarning(true);
        }
        setLinkedTableOptions(
          _.map(response.linkedTables, (a) => ({ label: a, value: a })),
        );
      });

      return cancel;
    }
  }, [formData.discoveryType, selectedSchema.name, loginConfigId]);

  //get linked attributes options
  useEffect(() => {
    if (
      formData.discoveryType === "Application" &&
      !isEmpty(formData.linkedTables)
    ) {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getServiceNowCmdbCiAttributes({
          id: loginConfigId,
          ticketSystemType: "SERVICE_NOW",
          linkedTables: formData.linkedTables,
        });
      promise.then((response) => {
        setLinkedAttributesOptions(
          _.uniqBy(
            _.map(response.linkedTableAttributes, (a) => ({
              label: a,
              value: a,
            })),
            "value",
          ),
        );
      });

      return cancel;
    }
  }, [formData.discoveryType, formData.linkedTables, loginConfigId]);

  const fields = useMemo(
    () => [
      {
        name: "tables",
        label: "CMDB_CI Table",
        placeholder: "CMDB_CI Table",
        colSpan: 12,
        ...(cmdbCiTableOptions
          ? cmdbCiTableOptions.length
            ? { type: FieldTypes.SELECT, options: cmdbCiTableOptions || [] }
            : {
                help: "Configured users would need access to sys_db_object to populate the list. Otherwise, provide the table name.",
              }
          : {}),
        disabled: isEdit,
        required: true,
        props: {
          onChange: (
            value: string,
            listField: any,
            selectedOption: { label: string; value: number },
          ) => {
            // in case of table's data is empty we are showing simple text field, where valueis HTML event
            value =
              // @ts-expect-error
              value && typeof value === "string" ? value : value?.target.value;
            // BE expects schema as id, name value pair, in case of Service Now, CMDB tables does not have id name value pair so setting id as null
            setSelectedSchema({
              id: null,
              name: value,
            });
            form.setFieldsValue({
              attributes: [],
              linkedTables: [],
              linkedTableAttributes: [],
            });
          },
        },
      },
      ...(formData.discoveryType === "Application"
        ? [
            {
              name: "linkedTables",
              label: "Linked Tables",
              colSpan: 12,
              type: FieldTypes.MULTI_SELECT,
              options: linkedTableOptions || [],
              help: "Linked tables are shown as <fieldname>/<tablename>",
              rules: [
                {
                  validator: (_: any, values: string[]) => {
                    if (!isEmpty(values) && values.length > 10) {
                      return Promise.reject(
                        "Cannot select more than 10 Linked Tables",
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ],
              props: {
                onChange: (values: string[]) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    linkedTables: values,
                  }));
                  form.setFieldsValue({ linkedTableAttributes: [] });
                },
              },
            },
            ...(showLinkedTableWarning
              ? [
                  {
                    render: (props: any) => (
                      <div {...props} className="m-b-sm" />
                    ),
                    colSpan: 12,
                  },
                  {
                    type: FieldTypes.LABEL,
                    render: (props: any) => (
                      <Alert
                        message={null}
                        description="You might not see all the linked tables due to lack of access to sys_dictionary table API."
                        type="warning"
                        style={{ padding: "8px 8px" }}
                        className="m-b-sm"
                      />
                    ),
                    colSpan: 12,
                  },
                ]
              : []),
          ]
        : []),
      ...(formData.discoveryType === "Asset" &&
      featureFlags.SNOW_ASSETS_MULTI_LEVEL_ATTRIBUTES
        ? []
        : [
            {
              name: "attributes",
              label: "CMDB_CI Attributes",
              colSpan: 12,
              type: FieldTypes.MULTI_SELECT,
              options: cmdbCiAttributeOptions || [],
              required: true,
            },
          ]),
      ...(formData.discoveryType === "Application"
        ? [
            {
              name: "linkedTableAttributes",
              label: "Linked Table Attributes",
              type: FieldTypes.MULTI_SELECT,
              colSpan: 12,
              options: linkedAttributesOptions || [],
              disabled: isEmpty(formData?.linkedTables),
              rules: [
                {
                  required: !isEmpty(formData?.linkedTables),
                  message:
                    "Linked Table Attributes cannot be empty if any linked tables are selected",
                },
              ],
              props: {
                onChange: (values: string[]) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    linkedTableAttributes: values,
                  }));
                },
              },
            },
          ]
        : []),
      ...(featureFlags.SNOW_ASSETS_MULTI_LEVEL_ATTRIBUTES &&
      formData.discoveryType === "Asset"
        ? [
            {
              type: FieldTypes.TEXT_AREA,
              name: "multiLevelAttributes",
              placeholder:
                "Input all required and nested attributes as comma-separated values",
              label: (
                <>
                  Input Required Attributes{" "}
                  {selectedSchema.name
                    ? `for CMDB_CI<${selectedSchema.name}>`
                    : ""}
                </>
              ),
              help: (
                <>
                  Input all required and nested attributes as comma-separated
                  values.
                  <br />
                  Example:
                  sys_id,name,assignement_group.email,location.name,owned_by_.department.sys_id,assigned_to.center.dep.dep_name
                </>
              ),
              colSpan: 18,
              required: true,
              NoDefaultLengthRule: true,
              props: {
                onChange: (value: string) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    multiLevelAttributes: value,
                  }));
                },
              },
            },
          ]
        : []),
      {
        name: "discoveryType",
        label: "Type",
        colSpan: 12,
        disabled: isEdit,
        type: FieldTypes.SELECT,
        required: true,
        options: [
          { label: "Asset", value: "Asset" },
          { label: "Application", value: "Application" },
        ],
        props: {
          onChange: (value: string) => {
            setFormData((prevData) => ({ ...prevData, discoveryType: value }));
          },
        },
      },
      {
        name: "cmdbQuery",
        label: (
          <>
            CMDB Query {selectedSchema.name ? `for ${selectedSchema.name}` : ""}
          </>
        ),
        type: FieldTypes.TEXT_AREA,
        NoDefaultLengthRule: true,
        placeholder: "CMDB Query",
        rules: [{ max: 2000, message: "Maximum length is 2000 characters" }],
        colSpan: 18,
        help: "Enter the query used in the ServiceNow table search. To copy the query, run it in ServiceNow, then right-click on the executed query and select Copy query. Paste the copied query into this field.",
        props: {
          onChange: (value: string) => {
            setFormData((prevData) => ({ ...prevData, cmdbQuery: value }));
          },
        },
      },
    ],
    [
      cmdbCiAttributeOptions,
      cmdbCiTableOptions,
      form,
      isEdit,
      formData.discoveryType,
      formData?.linkedTables,
      selectedSchema.name,
      linkedAttributesOptions,
      linkedTableOptions,
      showLinkedTableWarning,
      featureFlags.SNOW_ASSETS_MULTI_LEVEL_ATTRIBUTES,
    ],
  );

  const onSave = useCallback(
    async (_formData) => {
      await form.validateFields();
      const payload = {
        id: data?.id,
        loginId: loginConfigId,
        toolName: "SERVICE_NOW",
        discoveryType: _formData.discoveryType.toUpperCase(),
        // BE expects schema as id, name value pair, in case of Service Now, CMDB tables does not have id name value pair so sending id as null
        schema: { id: null, name: selectedSchema.name },
        attributes: _formData.attributes,
        multiLevelAttributes: _formData.multiLevelAttributes,
        ...(_formData.linkedTables && {
          linkedTables: _formData.linkedTables,
        }),
        ...(_formData.linkedTableAttributes && {
          linkedTableAttributes: _formData.linkedTableAttributes,
        }),
        ...(_formData.cmdbQuery && {
          cmdbQuery: _formData.cmdbQuery,
        }),
      };
      await ToolsIntegrationsAPI[
        isEdit ? "updateJiraCmdbMapping" : "saveJiraCmdbMapping"
      ](payload).promise;
      message.success(
        isEdit ? "Config updated successfully" : "Config created successfully",
      );
      props.onUpdateConfigComponent?.();
    },
    [form, loginConfigId, isEdit, props, data?.id, selectedSchema.name],
  );

  return (
    <BaseModalForm
      afterClose={props.onUpdateConfigComponent}
      modalProps={{
        width: MODAL_SIZE_LARGE,
        okButtonProps: {
          permission: isEdit
            ? PermissionSetMap.EDIT_TOOL_CONFIG
            : PermissionSetMap.WRITE_TOOLS,
          ...(props?.okButtonProps || {}),
        },
      }}
      title={
        isEdit
          ? "Edit CMDB Discovery Assets/Applications"
          : "Create CMDB Discovery Assets/Applications"
      }
      onOk={onSave}
      fields={fields}
      form={form}
      initialValues={formData}
      ref={ref}
      {...props}
    />
  );
};

export default forwardRef(ServiceNowCmdbMappingForm);

export function ServiceNowCmdbConfigListingTable({
  data,
  loginConfigId,
  onEditConfig,
  onUpdateConfigComponent,
  ...props
}: ServiceNowCmdbConfigListProps) {
  const featureFlags = useFeatureFlag();
  const onDelete = useCallback(
    async (record) => {
      const payload = { discoveryDetails: [record] };
      try {
        await ToolsIntegrationsAPI.deleteServiceNowCmdbCiConfig(
          loginConfigId,
          payload,
        ).promise;
        if (onUpdateConfigComponent) {
          onUpdateConfigComponent();
        }
        message.success("CMDB Configuration deleted successfully.");
      } catch (err) {
        console.log(err);
      }
    },
    [onUpdateConfigComponent, loginConfigId],
  );

  const onClick = useCallback(
    async (_: any, record: any) => {
      switch (record.name) {
        case "edit":
          onEditConfig && onEditConfig(record.data);
          break;
        case "delete":
          await onDelete(record.data);
          break;
        default:
          break;
      }
    },
    [onEditConfig, onDelete],
  );

  const items = useMemo(
    () => [
      {
        type: "edit",
      },
      {
        type: "delete",
        permission: PermissionSetMap.DELETE_TOOL,
      },
    ],
    [],
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Table",
        dataIndex: "schema",
        width: 300,
        render: (
          schema: { id: number; name: string },
          record: ServiceNowCmdbDataProps,
        ) => {
          const tableValues: string[] =
            record &&
            record.discoveryType === "APPLICATION" &&
            record.linkedTables?.length
              ? [schema.name, ...(record.linkedTables || [])]
              : [];
          if (!isEmpty(tableValues)) {
            return columnRenderArrayData(tableValues.join());
          }
          return (
            <Typography.Text
              className="text-ellipsis"
              style={{ maxWidth: "300px", display: "inline-block" }}
              title={schema.name}
            >
              {CellRenderers.VALUE_OR_NA(schema.name)}
            </Typography.Text>
          );
        },
      },
      {
        title: "Attributes",
        dataIndex: "attributes",
        width: 300,
        render: (attributes: string[], record: ServiceNowCmdbDataProps) => {
          if (
            featureFlags.SNOW_ASSETS_MULTI_LEVEL_ATTRIBUTES &&
            record.multiLevelAttributes
          ) {
            return columnRenderArrayData(record.multiLevelAttributes);
          }
          if (!isEmpty(attributes)) {
            const attributeValues = [
              ...attributes,
              ...(record.discoveryType === "APPLICATION" &&
              record.linkedTableAttributes
                ? record.linkedTableAttributes
                : []),
            ];
            return columnRenderArrayData(attributeValues.join());
          }
          return CellRenderers.VALUE_OR_NA();
        },
      },
      {
        title: "Type",
        width: 100,
        dataIndex: "discoveryType",
        render: _.capitalize,
      },
      {
        title: "Action",
        key: "action",
        dataIndex: "action",
        width: 50,
        render: (_: any, record: any) => {
          const ActionMenu =
            ActionMenuV2 as React.ComponentType<ActionMenuProps>;
          return <ActionMenu items={items} onClick={onClick} data={record} />;
        },
      },
    ];
  }, [items, onClick, featureFlags.SNOW_ASSETS_MULTI_LEVEL_ATTRIBUTES]);

  const TableComponent =
    TableContainer as React.ComponentType<TableContainerProps>;
  return (
    <TableComponent dataSource={data} columns={columns} enableScroll={true} />
  );
}
